/**
 * ============================================================
 * KEJJA OS — src/pages/finance/FinanceOverviewPage.tsx  (v1.2)
 * The Money-Sexy Dashboard
 * ============================================================
 *
 * Order (mobile-first, stacks top → bottom):
 *   1. Safe to Spend Today        (unchanged hero)
 *   2. Net Worth + Savings Rate    (NEW — 2-col grid)
 *   3. Cashflow this month         (NEW — income vs out bars)
 *   4. This month's budget         (NEW)
 *   5. Spending donut + 30-day spark (NEW — 2-col)
 *   6. Coming up (bills)
 *   7. Credit exposure
 *   8. Saving for (funds)
 *   9. This week
 *  10. Where your money sits
 *  11. Recent activity
 * ============================================================
 */

import { Link } from 'react-router-dom';
import {
  ArrowDownCircle, ArrowUpCircle, AlertTriangle, Calendar,
  Target, TrendingUp, TrendingDown, Wallet, Sparkles,
  Plus, Smartphone, Zap, PiggyBank, Activity,
} from 'lucide-react';
import {
  PieChart, Pie, Cell, ResponsiveContainer,
  AreaChart, Area, XAxis, YAxis, Tooltip as RechartsTooltip,

} from 'recharts';

import { useFinanceContext } from './FinanceLayout';
import {
  usePulse, useSeedFinance, useFinanceCategories, useMarkBillPaid,
} from '@/hooks/useFinance';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { formatMoney, formatMoneyCompact } from '@/lib/formatMoney';
import { cn } from '@/lib/utils';

export default function FinanceOverviewPage() {
  const { homeId, currencyCode } = useFinanceContext();
  const { data: pulse, isLoading } = usePulse(homeId, currencyCode);
  const { data: cats } = useFinanceCategories(homeId);
  const seed = useSeedFinance(homeId);
  const markPaid = useMarkBillPaid(homeId);

  const isFirstRun = !isLoading && cats && cats.categories.length === 0;

  if (isFirstRun) {
    return (
      <Card className="border-dashed border-orange-500/30 bg-gradient-to-br from-orange-950/30 to-amber-950/20">
        <CardContent className="p-8 text-center">
          <div className="w-14 h-14 rounded-2xl bg-orange-100 dark:bg-orange-950/40 text-orange-700 dark:text-orange-300 mx-auto flex items-center justify-center mb-4">
            <Sparkles className="w-6 h-6" />
          </div>
          <h3 className="font-semibold text-lg text-foreground">Let's set up your money base</h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
            We'll create starter categories and two default accounts (Cash, M-Pesa). Rename or delete anything later.
          </p>
          <Button
            size="lg"
            className="mt-5 bg-orange-600 hover:bg-orange-700 text-white"
            onClick={() => seed.mutate()}
            disabled={seed.isPending}
          >
            {seed.isPending ? 'Setting up…' : 'Set up Finance defaults'}
          </Button>
          {seed.isError && (
            <p className="text-xs text-rose-600 mt-3">
              {(seed.error as Error)?.message ?? 'Could not seed. Try again.'}
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  if (isLoading || !pulse) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-40 rounded-2xl" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Skeleton className="h-32 rounded-xl" />
          <Skeleton className="h-32 rounded-xl" />
        </div>
        <Skeleton className="h-48 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-5 pb-12">
      <SafeToSpendHero pulse={pulse} />

      {/* Net worth + savings rate — the "how am I doing" row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <NetWorthCard pulse={pulse} />
        <SavingsRateCard pulse={pulse} />
      </div>

      {/* Cashflow this month */}
      {(pulse.cashflow.in > 0 || pulse.cashflow.out > 0) && (
        <CashflowSection pulse={pulse} />
      )}

      {/* Budget */}
      {pulse.budget.totalBudget > 0 && <BudgetSection pulse={pulse} />}

      {/* Charts — category donut + 30-day spark */}
      {pulse.spendByCategory.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <CategoryDonut pulse={pulse} />
          <SpendTrendCard pulse={pulse} />
        </div>
      )}

      {/* Upcoming bills */}
      <UpcomingBillsSection pulse={pulse} onMarkPaid={(id) => markPaid.mutate(id)} />

      {/* Credit */}
      {pulse.credit.lines.length > 0 && <CreditSection pulse={pulse} />}

      {/* Funds */}
      {pulse.funds.length > 0 && <FundsSection pulse={pulse} />}

      <WeekRhythmSection pulse={pulse} />
      <LiquidityBreakdown pulse={pulse} />
      <RecentActivity pulse={pulse} />

      <div className="pt-2 grid grid-cols-2 gap-3">
        <Button asChild variant="outline" className="h-auto py-3 justify-start">
          <Link to="/finance/transactions?add=1">
            <Plus className="w-4 h-4 mr-2" />
            <div className="text-left">
              <div className="text-sm font-medium">Add transaction</div>
              <div className="text-[11px] text-muted-foreground">Manual entry</div>
            </div>
          </Link>
        </Button>
        <Button asChild variant="outline" className="h-auto py-3 justify-start">
          <Link to="/finance/mpesa">
            <Smartphone className="w-4 h-4 mr-2" />
            <div className="text-left">
              <div className="text-sm font-medium">Paste M-Pesa SMS</div>
              <div className="text-[11px] text-muted-foreground">Instant import</div>
            </div>
          </Link>
        </Button>
      </div>
    </div>
  );
}

/* ======================================================================
 * 1. Safe to Spend
 * ====================================================================== */

function SafeToSpendHero({ pulse }: { pulse: NonNullable<ReturnType<typeof usePulse>['data']> }) {
  const safe = pulse.safeToSpend;
  const liquidity = pulse.liquidity.total;
  const bills = pulse.upcomingBills.items.filter((b) => b.isHardBill).reduce((s, b) => s + b.amount, 0);

  const tone =
    safe < 0 ? 'danger' :
    safe < liquidity * 0.15 ? 'tight' :
    'ok';

  const toneStyles = {
    danger: 'from-rose-500 to-rose-600 text-white',
    tight: 'from-amber-500 to-orange-500 text-white',
    ok: 'from-orange-500 via-orange-600 to-amber-600 text-white',
  }[tone];

  const toneLabel = {
    danger: 'You are over budget',
    tight: 'Spend carefully',
    ok: 'Safe to spend today',
  }[tone];

  return (
    <Card className={cn('overflow-hidden border-0 shadow-lg bg-gradient-to-br', toneStyles)}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest opacity-90">
              {toneLabel}
            </div>
            <div className="text-4xl md:text-5xl font-bold tabular-nums tracking-tight mt-2">
              {formatMoney(safe, pulse.currencyCode)}
            </div>
          </div>
          {tone === 'danger' && <AlertTriangle className="w-6 h-6 opacity-80" />}
          {tone === 'tight' && <Zap className="w-6 h-6 opacity-80" />}
          {tone === 'ok' && <Wallet className="w-6 h-6 opacity-80" />}
        </div>

        <div className="mt-5 flex items-center gap-4 text-xs font-medium">
          <div>
            <div className="opacity-70">You have</div>
            <div className="font-bold tabular-nums">{formatMoney(liquidity, pulse.currencyCode)}</div>
          </div>
          <div className="text-lg opacity-50">−</div>
          <div>
            <div className="opacity-70">Bills next {pulse.upcomingBills.withinDays}d</div>
            <div className="font-bold tabular-nums">{formatMoney(bills, pulse.currencyCode)}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* ======================================================================
 * 2. Net Worth Card
 * ====================================================================== */

function NetWorthCard({ pulse }: { pulse: NonNullable<ReturnType<typeof usePulse>['data']> }) {
  const { current, cash, savings, debt, delta, deltaPercent } = pulse.netWorth;
  const up = delta >= 0;

  // stacked bar composition
  const total = cash + savings + Math.abs(debt);
  const cashPct = total > 0 ? (cash / total) * 100 : 0;
  const savingsPct = total > 0 ? (savings / total) * 100 : 0;
  const debtPct = total > 0 ? (Math.abs(debt) / total) * 100 : 0;

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
            Net worth
          </h3>
          {(cash + savings + debt) > 0 && (
            <div className={cn(
              'flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold',
              up
                ? 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300'
                : 'bg-rose-100 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300',
            )}>
              {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {up ? '+' : ''}{deltaPercent}%
            </div>
          )}
        </div>

        <div className="text-2xl md:text-3xl font-bold tabular-nums tracking-tight text-foreground">
          {formatMoney(current, pulse.currencyCode)}
        </div>
        <div className="text-[10px] text-muted-foreground mt-0.5">
          {up ? '+' : ''}{formatMoney(delta, pulse.currencyCode)} this month
        </div>

        {/* Stacked bar: cash | savings | debt */}
        {total > 0 && (
          <div className="mt-3">
            <div className="h-2 rounded-full bg-muted overflow-hidden flex">
              <div className="h-full bg-emerald-500" style={{ width: `${cashPct}%` }} />
              <div className="h-full bg-sky-500" style={{ width: `${savingsPct}%` }} />
              <div className="h-full bg-rose-500" style={{ width: `${debtPct}%` }} />
            </div>
            <div className="flex items-center gap-3 mt-2 text-[10px]">
              <LegendDot color="bg-emerald-500" label="Cash" value={formatMoneyCompact(cash, pulse.currencyCode)} />
              <LegendDot color="bg-sky-500" label="Savings" value={formatMoneyCompact(savings, pulse.currencyCode)} />
              {debt > 0 && (
                <LegendDot color="bg-rose-500" label="Debt" value={formatMoneyCompact(debt, pulse.currencyCode)} />
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function LegendDot({ color, label, value }: { color: string; label: string; value: string }) {
  return (
    <span className="flex items-center gap-1">
      <span className={cn('w-2 h-2 rounded-full', color)} />
      <span className="text-muted-foreground">{label}</span>
      <span className="tabular-nums font-semibold text-foreground">{value}</span>
    </span>
  );
}

/* ======================================================================
 * 2b. Savings Rate Card — ring gauge
 * ====================================================================== */

function SavingsRateCard({ pulse }: { pulse: NonNullable<ReturnType<typeof usePulse>['data']> }) {
  const rate = pulse.savingsRate;
  const { toSavings, in: income } = pulse.cashflow;

  // 20% savings rate is the "good" benchmark
  const tone =
    rate >= 20 ? 'good' :
    rate >= 10 ? 'okay' :
    rate > 0 ? 'low' :
    'none';

  const ringColor = {
    good: 'text-emerald-500',
    okay: 'text-amber-500',
    low: 'text-rose-500',
    none: 'text-muted-foreground/30',
  }[tone];

  const toneLabel = {
    good: 'Healthy',
    okay: 'Building',
    low: 'Starting',
    none: 'No income tracked yet',
  }[tone];

  return (
    <Card>
      <CardContent className="p-4">
        <h3 className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
          Savings rate
        </h3>

        <div className="flex items-center gap-4 mt-2">
          <ProgressRing pct={Math.min(100, rate)} label={`${rate}%`} className={ringColor} />
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              This month
            </div>
            <div className="text-lg font-bold tabular-nums text-foreground mt-0.5">
              {formatMoney(toSavings, pulse.currencyCode)}
            </div>
            <div className="text-[10px] text-muted-foreground">
              of {formatMoney(income, pulse.currencyCode)} income
            </div>
            <div className={cn(
              'text-[10px] font-semibold mt-1 uppercase tracking-wider',
              tone === 'good' && 'text-emerald-600 dark:text-emerald-400',
              tone === 'okay' && 'text-amber-600 dark:text-amber-400',
              tone === 'low' && 'text-rose-600 dark:text-rose-400',
              tone === 'none' && 'text-muted-foreground',
            )}>
              {toneLabel}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ProgressRing({ pct, label, className }: { pct: number; label: string; className?: string }) {
  const radius = 28;
  const stroke = 6;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (pct / 100) * circ;
  const size = 72;
  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
      <svg viewBox={`0 0 ${size} ${size}`} className={cn('w-full h-full -rotate-90', className)}>
        <circle cx={size / 2} cy={size / 2} r={radius}
          stroke="currentColor" strokeWidth={stroke} fill="none"
          className="opacity-20" />
        <circle cx={size / 2} cy={size / 2} r={radius}
          stroke="currentColor" strokeWidth={stroke} fill="none"
          strokeDasharray={circ} strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-500" />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center text-sm font-bold tabular-nums text-foreground">
        {label}
      </div>
    </div>
  );
}

/* ======================================================================
 * 3. Cashflow this month
 * ====================================================================== */

function CashflowSection({ pulse }: { pulse: NonNullable<ReturnType<typeof usePulse>['data']> }) {
  const { in: inAmt, out, toSavings, net } = pulse.cashflow;
  const maxVal = Math.max(inAmt, out, 1);
  const netUp = net >= 0;

  return (
    <section>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
          <Activity className="w-3.5 h-3.5" /> Cashflow this month
        </h2>
      </div>
      <Card>
        <CardContent className="p-4 space-y-3">
          {/* In bar */}
          <div>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="font-medium text-emerald-700 dark:text-emerald-300">Money in</span>
              <span className="font-bold tabular-nums">{formatMoney(inAmt, pulse.currencyCode)}</span>
            </div>
            <div className="h-3 rounded-full bg-muted overflow-hidden">
              <div className="h-full bg-emerald-500 rounded-full transition-all"
                   style={{ width: `${(inAmt / maxVal) * 100}%` }} />
            </div>
          </div>

          {/* Out bar */}
          <div>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="font-medium text-foreground">Money out</span>
              <span className="font-bold tabular-nums">{formatMoney(out, pulse.currencyCode)}</span>
            </div>
            <div className="h-3 rounded-full bg-muted overflow-hidden">
              <div className="h-full bg-orange-500 rounded-full transition-all"
                   style={{ width: `${(out / maxVal) * 100}%` }} />
            </div>
          </div>

          {/* Saved (subset of out) */}
          {toSavings > 0 && (
            <div>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="font-medium text-sky-700 dark:text-sky-300 flex items-center gap-1">
                  <PiggyBank className="w-3 h-3" /> Saved
                </span>
                <span className="font-bold tabular-nums">{formatMoney(toSavings, pulse.currencyCode)}</span>
              </div>
              <div className="h-3 rounded-full bg-muted overflow-hidden">
                <div className="h-full bg-sky-500 rounded-full transition-all"
                     style={{ width: `${(toSavings / maxVal) * 100}%` }} />
              </div>
            </div>
          )}

          {/* Net */}
          <div className="pt-2 mt-1 border-t border-border flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Net
            </span>
            <span className={cn(
              'text-lg font-bold tabular-nums',
              netUp ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400',
            )}>
              {netUp ? '+' : ''}{formatMoney(net, pulse.currencyCode)}
            </span>
          </div>
        </CardContent>
      </Card>
    </section>
  );
}

/* ======================================================================
 * 4. Budget section
 * ====================================================================== */

function BudgetSection({ pulse }: { pulse: NonNullable<ReturnType<typeof usePulse>['data']> }) {
  const { totalBudget, totalSpent, percentUsed, categoryCount, overCategoryCount, month } = pulse.budget;
  const remaining = totalBudget - totalSpent;
  const over = totalSpent > totalBudget;
  const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  return (
    <section>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
          <Target className="w-3.5 h-3.5" /> {MONTH_NAMES[month - 1]} budget
        </h2>
        <Link to="/finance/budgets" className="text-xs font-medium text-orange-700 dark:text-orange-300 hover:underline">
          Manage →
        </Link>
      </div>
      <Card className={cn(over ? 'border-rose-300 dark:border-rose-900/40' : '')}>
        <CardContent className="p-4 space-y-2.5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Budgeted
              </div>
              <div className="text-xl md:text-2xl font-bold tabular-nums text-foreground">
                {formatMoney(totalBudget, pulse.currencyCode)}
              </div>
              <div className="text-[10px] text-muted-foreground mt-0.5">
                {categoryCount} categor{categoryCount === 1 ? 'y' : 'ies'}
                {overCategoryCount > 0 && (
                  <span className="text-rose-600 dark:text-rose-400 font-semibold ml-1">
                    · {overCategoryCount} over
                  </span>
                )}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Spent
              </div>
              <div className={cn(
                'text-xl md:text-2xl font-bold tabular-nums',
                over ? 'text-rose-600 dark:text-rose-400' : 'text-foreground',
              )}>
                {formatMoney(totalSpent, pulse.currencyCode)}
              </div>
            </div>
          </div>
          <div className="h-2 rounded-full bg-muted overflow-hidden">
            <div className={cn(
              'h-full rounded-full transition-all',
              over ? 'bg-rose-500' : percentUsed > 80 ? 'bg-amber-500' : 'bg-emerald-500',
            )} style={{ width: `${Math.min(100, percentUsed)}%` }} />
          </div>
          <div className="flex items-center justify-between text-[11px]">
            <span className={cn(
              'tabular-nums font-medium',
              over ? 'text-rose-600 dark:text-rose-400' : 'text-muted-foreground',
            )}>
              {percentUsed}% used
            </span>
            <span className="tabular-nums text-muted-foreground">
              {over
                ? `${formatMoney(totalSpent - totalBudget, pulse.currencyCode)} over`
                : `${formatMoney(remaining, pulse.currencyCode)} left`}
            </span>
          </div>
        </CardContent>
      </Card>
    </section>
  );
}

/* ======================================================================
 * 5a. Spending by category — donut
 * ====================================================================== */

function CategoryDonut({ pulse }: { pulse: NonNullable<ReturnType<typeof usePulse>['data']> }) {
  const data = pulse.spendByCategory;
  const totalOut = data.reduce((s, d) => s + d.amount, 0);

  return (
    <Card>
      <CardContent className="p-4">
        <h3 className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground mb-3">
          Where it's going
        </h3>

        <div className="relative h-40">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="amount"
                nameKey="name"
                innerRadius="60%"
                outerRadius="90%"
                stroke="none"
                paddingAngle={2}
              >
                {data.map((d, i) => (
                  <Cell key={i} fill={d.color ?? '#6b7280'} />
                ))}
              </Pie>
              <RechartsTooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const p = payload[0]!.payload;
                  return (
                    <div className="bg-background border border-border rounded-lg px-3 py-2 text-xs shadow-lg">
                      <div className="font-semibold">{p.name}</div>
                      <div className="tabular-nums text-muted-foreground">
                        {formatMoney(p.amount, pulse.currencyCode)} · {p.percent}%
                      </div>
                    </div>
                  );
                }}
              />
            </PieChart>
          </ResponsiveContainer>
          {/* Center label */}
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <div className="text-[9px] uppercase tracking-widest text-muted-foreground">
              Total out
            </div>
            <div className="text-sm font-bold tabular-nums">
              {formatMoneyCompact(totalOut, pulse.currencyCode)}
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="mt-2 space-y-1">
          {data.slice(0, 5).map((d) => (
            <div key={d.categoryId ?? d.name} className="flex items-center gap-2 text-[11px]">
              <span className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: d.color ?? '#6b7280' }} />
              <span className="flex-1 truncate">{d.name}</span>
              <span className="tabular-nums text-muted-foreground">{d.percent}%</span>
            </div>
          ))}
          {data.length > 5 && (
            <Link to="/finance/transactions" className="text-[10px] text-orange-700 dark:text-orange-300 hover:underline">
              See all →
            </Link>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/* ======================================================================
 * 5b. 30-day spend trend — area spark
 * ====================================================================== */

function SpendTrendCard({ pulse }: { pulse: NonNullable<ReturnType<typeof usePulse>['data']> }) {
  const data = pulse.last30DaysSpend.map((d) => ({
    date: d.date,
    amount: d.amount / 100,    // to major for the chart axis
    amountMinor: d.amount,
  }));
  const total = pulse.last30DaysSpend.reduce((s, d) => s + d.amount, 0);
  const avgPerDay = total / 30;
  const today = data[data.length - 1]?.amountMinor ?? 0;
  const yday = data[data.length - 2]?.amountMinor ?? 0;
  const delta = today - yday;

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
            30-day spend
          </h3>
          <span className="text-[10px] text-muted-foreground tabular-nums">
            avg {formatMoneyCompact(avgPerDay, pulse.currencyCode)}/day
          </span>
        </div>

        <div className="text-xl font-bold tabular-nums text-foreground">
          {formatMoney(total, pulse.currencyCode)}
        </div>
        <div className="text-[10px] text-muted-foreground mb-2">
          past 30 days
        </div>

        <div className="h-24 -mx-2">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data}>
              <defs>
                <linearGradient id="spendGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f97316" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#f97316" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" hide />
              <YAxis hide />
              <RechartsTooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const p = payload[0]!.payload;
                  return (
                    <div className="bg-background border border-border rounded-lg px-2 py-1 text-[11px] shadow-lg">
                      <div className="font-semibold">{p.date}</div>
                      <div className="tabular-nums">{formatMoney(p.amountMinor, pulse.currencyCode)}</div>
                    </div>
                  );
                }}
              />
              <Area
                type="monotone"
                dataKey="amount"
                stroke="#f97316"
                strokeWidth={2}
                fill="url(#spendGrad)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {yday > 0 && (
          <div className="flex items-center gap-1 text-[10px] text-muted-foreground mt-1">
            {delta > 0 ? <TrendingUp className="w-3 h-3 text-rose-500" /> : <TrendingDown className="w-3 h-3 text-emerald-500" />}
            Today {delta > 0 ? '+' : ''}{formatMoneyCompact(delta, pulse.currencyCode)} vs yesterday
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ======================================================================
 * 6. Upcoming Bills (unchanged from v1.1)
 * ====================================================================== */

function UpcomingBillsSection({
  pulse, onMarkPaid,
}: {
  pulse: NonNullable<ReturnType<typeof usePulse>['data']>;
  onMarkPaid: (id: string) => void;
}) {
  const bills = pulse.upcomingBills.items;
  const hasDue = bills.length > 0;

  return (
    <section>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
          <Calendar className="w-3.5 h-3.5" /> Coming up
        </h2>
        <Link to="/finance/bills" className="text-xs font-medium text-orange-700 dark:text-orange-300 hover:underline">
          All bills →
        </Link>
      </div>

      {!hasDue ? (
        <Card className="border-dashed">
          <CardContent className="p-5 text-center">
            <p className="text-sm text-muted-foreground">
              No bills due in the next {pulse.upcomingBills.withinDays} days.
            </p>
            <Button asChild variant="outline" size="sm" className="mt-3">
              <Link to="/finance/bills"><Plus className="w-3.5 h-3.5 mr-1" />Add a recurring bill</Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0 divide-y divide-border">
            {bills.map((b) => {
              const overdue = b.daysUntil < 0;
              const today = b.daysUntil === 0;
              const soon = b.daysUntil > 0 && b.daysUntil <= 3;

              const badge = overdue ? { txt: `${Math.abs(b.daysUntil)}d overdue`, cls: 'bg-rose-100 text-rose-700 border-rose-300 dark:bg-rose-950/40 dark:text-rose-300' }
                : today     ? { txt: 'TODAY',                                     cls: 'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-950/40 dark:text-amber-300' }
                : soon      ? { txt: `in ${b.daysUntil}d`,                         cls: 'bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-950/40 dark:text-orange-300' }
                : { txt: `in ${b.daysUntil}d`, cls: 'bg-muted text-muted-foreground' };

              return (
                <div key={b.id} className="p-4 flex items-center gap-3">
                  <div
                    className="h-10 w-10 rounded-xl flex items-center justify-center text-xs font-bold flex-shrink-0"
                    style={{
                      backgroundColor: b.color ? `${b.color}20` : 'var(--muted)',
                      color: b.color ?? 'var(--muted-foreground)',
                    }}
                  >
                    {b.name[0]?.toUpperCase() ?? '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm text-foreground truncate">{b.name}</span>
                      {!b.isHardBill && (
                        <Badge variant="outline" className="text-[9px] font-normal">soft</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className={cn('text-[10px] px-1.5 py-0.5 rounded border font-medium uppercase tracking-wider', badge.cls)}>
                        {badge.txt}
                      </span>
                      <span className="text-[11px] text-muted-foreground tabular-nums">
                        {formatMoney(b.amount, pulse.currencyCode)}
                      </span>
                    </div>
                  </div>
                  <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => onMarkPaid(b.id)}>
                    Paid
                  </Button>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
    </section>
  );
}

/* ======================================================================
 * 7. Credit (unchanged)
 * ====================================================================== */

function CreditSection({ pulse }: { pulse: NonNullable<ReturnType<typeof usePulse>['data']> }) {
  const { totalUsed, totalLimit, lines } = pulse.credit;
  return (
    <section>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
          <AlertTriangle className="w-3.5 h-3.5" /> Credit exposure
        </h2>
        <Link to="/finance/credit" className="text-xs font-medium text-orange-700 dark:text-orange-300 hover:underline">
          Manage →
        </Link>
      </div>
      <Card className={cn(totalUsed > 0 ? 'border-rose-300 bg-rose-50/50 dark:bg-rose-950/10' : '')}>
        <CardContent className="p-4 space-y-3">
          {lines.map((c) => {
            const isHigh = c.percentUsed > 80;
            return (
              <div key={c.id}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{c.name}</span>
                    <Badge variant="outline" className="text-[9px] font-normal uppercase">{c.kind.replace('_', ' ')}</Badge>
                  </div>
                  <span className={cn(
                    'text-sm font-semibold tabular-nums',
                    c.usedAmount > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-muted-foreground',
                  )}>
                    {formatMoney(c.usedAmount, pulse.currencyCode)}
                    {c.limitAmount > 0 && (
                      <span className="text-muted-foreground font-normal"> / {formatMoneyCompact(c.limitAmount, pulse.currencyCode)}</span>
                    )}
                  </span>
                </div>
                {c.limitAmount > 0 && (
                  <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                    <div className={cn(
                      'h-full rounded-full transition-all',
                      isHigh ? 'bg-rose-500' : c.percentUsed > 50 ? 'bg-amber-500' : 'bg-emerald-500',
                    )} style={{ width: `${Math.min(100, c.percentUsed)}%` }} />
                  </div>
                )}
              </div>
            );
          })}
          {totalLimit > 0 && (
            <div className="pt-2 border-t border-border text-[11px] text-muted-foreground flex justify-between">
              <span>Total outstanding</span>
              <span className="font-semibold tabular-nums text-foreground">
                {formatMoney(totalUsed, pulse.currencyCode)} of {formatMoneyCompact(totalLimit, pulse.currencyCode)}
              </span>
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  );
}

/* ======================================================================
 * 8. Funds
 * ====================================================================== */

function FundsSection({ pulse }: { pulse: NonNullable<ReturnType<typeof usePulse>['data']> }) {
  return (
    <section>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
          <Target className="w-3.5 h-3.5" /> Saving for
        </h2>
        <Link to="/finance/funds" className="text-xs font-medium text-orange-700 dark:text-orange-300 hover:underline">
          All goals →
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {pulse.funds.map((f) => (
          <Card key={f.id} className="overflow-hidden">
            <div className="h-1" style={{ backgroundColor: f.color ?? '#f97316' }} />
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-semibold text-sm truncate">{f.name}</div>
                  {f.daysUntilTarget !== null && (
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      {f.daysUntilTarget < 0
                        ? `${Math.abs(f.daysUntilTarget)}d overdue`
                        : f.daysUntilTarget === 0
                        ? 'Due today'
                        : `${f.daysUntilTarget} days to go`}
                    </div>
                  )}
                </div>
                <span className="text-[11px] font-bold tabular-nums text-muted-foreground">{f.percent}%</span>
              </div>
              <div className="mt-3 h-2 rounded-full bg-muted overflow-hidden">
                <div className="h-full rounded-full transition-all"
                     style={{ width: `${Math.min(100, f.percent)}%`, backgroundColor: f.color ?? '#f97316' }} />
              </div>
              <div className="mt-2 flex items-center justify-between text-[11px] tabular-nums">
                <span className="font-semibold text-foreground">
                  {formatMoneyCompact(f.savedAmount, pulse.currencyCode)}
                </span>
                <span className="text-muted-foreground">
                  / {formatMoneyCompact(f.targetAmount, pulse.currencyCode)}
                </span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
}

/* ======================================================================
 * 9. Week Rhythm
 * ====================================================================== */

function WeekRhythmSection({ pulse }: { pulse: NonNullable<ReturnType<typeof usePulse>['data']> }) {
  const { out, avgWeeklyOut, deltaVsAvg, txCount } = pulse.week;
  const pct = avgWeeklyOut > 0 ? Math.round((out / avgWeeklyOut) * 100) : 0;
  const higher = deltaVsAvg > 0;

  return (
    <section>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          This week
        </h2>
      </div>
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold tabular-nums text-foreground">
                  {formatMoney(out, pulse.currencyCode)}
                </span>
                <span className="text-xs text-muted-foreground">spent</span>
              </div>
              <div className="text-[11px] text-muted-foreground mt-1">
                {txCount} transaction{txCount === 1 ? '' : 's'} this week
              </div>
            </div>
            {avgWeeklyOut > 0 && (
              <div className={cn(
                'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold',
                higher
                  ? 'bg-rose-100 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300'
                  : 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300',
              )}>
                {higher ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                {higher ? '+' : ''}{pct - 100}% vs avg
              </div>
            )}
          </div>
          {avgWeeklyOut > 0 && (
            <div className="mt-3 h-1.5 rounded-full bg-muted overflow-hidden">
              <div className={cn(
                'h-full rounded-full transition-all',
                higher ? 'bg-rose-500' : 'bg-emerald-500',
              )} style={{ width: `${Math.min(100, pct)}%` }} />
            </div>
          )}
          <div className="text-[10px] text-muted-foreground mt-2">
            4-week average: {formatMoney(avgWeeklyOut, pulse.currencyCode)}
          </div>
        </CardContent>
      </Card>
    </section>
  );
}

/* ======================================================================
 * 10. Liquidity Breakdown
 * ====================================================================== */

function LiquidityBreakdown({ pulse }: { pulse: NonNullable<ReturnType<typeof usePulse>['data']> }) {
  return (
    <section>
      <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">
        Where your money sits
      </h2>
      <Card>
        <CardContent className="p-0 divide-y divide-border">
          {pulse.liquidity.byAccount.map((a) => (
            <div key={a.accountId} className="p-3 flex items-center gap-3">
              <div
                className="h-9 w-9 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0"
                style={{
                  backgroundColor: a.color ? `${a.color}20` : 'var(--muted)',
                  color: a.color ?? 'var(--muted-foreground)',
                }}
              >
                {a.name[0]?.toUpperCase() ?? '?'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm text-foreground">{a.name}</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {a.accountType}
                </div>
              </div>
              <span className={cn(
                'text-sm font-semibold tabular-nums flex-shrink-0',
                a.balance < 0 ? 'text-rose-600' : 'text-foreground',
              )}>
                {formatMoney(a.balance, pulse.currencyCode)}
              </span>
            </div>
          ))}
          <div className="p-3 flex items-center gap-3 bg-muted/30">
            <div className="h-9 w-9 rounded-lg bg-orange-100 dark:bg-orange-950/40 text-orange-700 dark:text-orange-300 flex items-center justify-center flex-shrink-0">
              <Wallet className="w-4 h-4" />
            </div>
            <div className="flex-1 font-semibold text-sm">Total</div>
            <span className="text-base font-bold tabular-nums">
              {formatMoney(pulse.liquidity.total, pulse.currencyCode)}
            </span>
          </div>
        </CardContent>
      </Card>
    </section>
  );
}

/* ======================================================================
 * 11. Recent
 * ====================================================================== */

function RecentActivity({ pulse }: { pulse: NonNullable<ReturnType<typeof usePulse>['data']> }) {
  if (pulse.recent.length === 0) return null;
  return (
    <section>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          Recent activity
        </h2>
        <Link to="/finance/transactions" className="text-xs font-medium text-orange-700 dark:text-orange-300 hover:underline">
          View all →
        </Link>
      </div>
      <Card>
        <CardContent className="p-0 divide-y divide-border">
          {pulse.recent.map((tx) => (
            <div key={tx.id} className="p-3 flex items-center gap-3">
              <div className={cn(
                'h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0',
                tx.direction === 'in'
                  ? 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300'
                  : 'bg-orange-100 dark:bg-orange-950/40 text-orange-700 dark:text-orange-300',
              )}>
                {tx.direction === 'in' ? <ArrowDownCircle className="w-4 h-4" /> : <ArrowUpCircle className="w-4 h-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-foreground truncate">
                  {tx.merchant ?? tx.description ?? 'Transaction'}
                </div>
                <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
                  <span>{new Date(tx.occurredAt).toLocaleDateString()}</span>
                  {tx.source === 'mpesa_sms' && (
                    <Badge variant="outline" className="text-[9px] h-4 px-1 font-normal">M-Pesa</Badge>
                  )}
                  {tx.usedCredit && (
                    <Badge variant="outline" className="text-[9px] h-4 px-1 font-semibold bg-rose-50 dark:bg-rose-950/20 border-rose-300 text-rose-700 dark:text-rose-300">
                      ⚠ Fuliza
                    </Badge>
                  )}
                </div>
              </div>
              <span className={cn(
                'text-sm font-semibold tabular-nums flex-shrink-0',
                tx.direction === 'in' ? 'text-emerald-600 dark:text-emerald-400' : 'text-foreground',
              )}>
                {tx.direction === 'in' ? '+' : '−'}{formatMoney(tx.amount, tx.currencyCode)}
              </span>
            </div>
          ))}
        </CardContent>
      </Card>
    </section>
  );
}
